import { useCallback, useEffect, useState } from 'react';
import { getCandidateScreeningProgressSummary } from '../../../service/VolunteerApplicationService';
import { useAuth } from '../../../hooks/useAuth';
import { useBatchId } from '../../../hooks/useBatchId';
import { useToastMessage } from '../../../hooks/useToastMessage';
import {
	Batch,
	CandidateScreeningProgressSummaryFilter,
	CandidateScreeningProgressSummaryResponse,
	PagingConfig,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useSnapshot } from 'valtio';
import {
	DashboardListState,
	ScreeningDashboardState,
} from './DashboardListStore';
import { DEFAULT_PAGE_SIZE } from '../../../shared/Constants';
import { displayError } from '../../../shared/Utils';
import useUserSession from '../../../hooks/useUserSession';

let lastCompletedFetchKey: string | null = null;
let inflightFetchKey: string | null = null;

export const useScreeningDashboard = () => {
	const { srcmId } = useAuth();
	const { selectedRole } = useUserSession();
	const { batchId } = useBatchId();
	const { showToastMessage } = useToastMessage();
	const filterStateSnap = useSnapshot(DashboardListState);
	const screeningSnap = useSnapshot(ScreeningDashboardState);
	const [paging, setPaging] = useState<PagingConfig>(() => {
		return { ...filterStateSnap.paging } as PagingConfig;
	});
	const [loading, setLoading] = useState<boolean>(false);

	const adminDashboardResponse = screeningSnap.adminDashboardResponse as
		| CandidateScreeningProgressSummaryResponse
		| undefined;
	const dashboardFilter =
		screeningSnap.dashboardFilter as CandidateScreeningProgressSummaryFilter;

	const onPageChange = useCallback((pageNumber: number, pageSize: number) => {
		DashboardListState.paging = { pageNo: pageNumber, pageSize };
		setPaging({ pageNo: pageNumber, pageSize });
	}, []);

	const onDashboardFilterChange = useCallback(
		(filterParam: CandidateScreeningProgressSummaryFilter) => {
			DashboardListState.filterBy = { ...filterParam };
			ScreeningDashboardState.dashboardFilter = { ...filterParam };
			onPageChange(1, paging.pageSize ?? DEFAULT_PAGE_SIZE);
		},
		[onPageChange, paging.pageSize]
	);

	useEffect(() => {
		if (!srcmId || !selectedRole) {
			return;
		}

		const selectedBatchDetails = sessionStorage.getItem('SELECTED_BATCH');
		const resolvedBatchId = selectedBatchDetails
			? ((JSON.parse(selectedBatchDetails) as Batch).id as number)
			: batchId;

		const fetchKey = JSON.stringify({
			srcmId,
			selectedRole,
			resolvedBatchId,
			filter: dashboardFilter,
		});

		if (fetchKey === lastCompletedFetchKey || fetchKey === inflightFetchKey) {
			return;
		}

		inflightFetchKey = fetchKey;
		setLoading(true);

		void (async () => {
			try {
				const screeningDashboardResponse =
					await getCandidateScreeningProgressSummary({
						functionarySrcmId: srcmId,
						batchId: resolvedBatchId,
						filter: dashboardFilter,
						selectedRoleCode: selectedRole,
					});
				if (screeningDashboardResponse) {
					ScreeningDashboardState.adminDashboardResponse =
						screeningDashboardResponse;
				}
				lastCompletedFetchKey = fetchKey;
			} catch (err) {
				const errorMsg = displayError(err);
				showToastMessage('error', errorMsg);
			} finally {
				inflightFetchKey = null;
				setLoading(false);
			}
		})();
	}, [dashboardFilter, srcmId, selectedRole, batchId, showToastMessage]);

	return {
		adminDashboardResponse,
		dashboardFilter,
		onDashboardFilterChange,
		paging,
		onPageChange,
		loading,
	};
};
